import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axiosConfig';

// Crear evento (admin)
export const createEvent = createAsyncThunk(
  'events/createEvent',
  async ({ slug, data }, { rejectWithValue }) => {             // ③
    try {
      const response = await axios.post(`/${slug}/api/events`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Obtener todos los eventos de un salón (slug)
export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (slug, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/${slug}/api/events`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Obtener TODOS los eventos (Global)
export const fetchGlobalEvents = createAsyncThunk(
  'events/fetchGlobalEvents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/events`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Obtener detalles de un evento de un salón (slug + id)
export const fetchEventDetails = createAsyncThunk(
  'events/fetchEventDetails',
  async ({ slug, id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/${slug}/api/events/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// **Actualizar** un evento
export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ slug, id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/${slug}/api/events/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Agrega un nuevo dia reutilizando la configuracion de un evento existente.
export const addEventDate = createAsyncThunk(
  'events/addEventDate',
  async ({ slug, id, date, allowScheduleConflict = false }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/${slug}/api/events/${id}/dates`, {
        date,
        allowScheduleConflict,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const mergeEventDate = createAsyncThunk(
  'events/mergeEventDate',
  async ({ slug, id, eventId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/${slug}/api/events/${id}/merge-date`, { eventId });
      return { event: response.data, mergedEventId: eventId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// **Eliminar** un evento
export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async ({ slug, id }, { rejectWithValue }) => {
    try {
      await axios.delete(`/${slug}/api/events/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const eventSlice = createSlice({
  name: 'events',
  initialState: {
    events: [],
    eventDetails: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Crear evento
      .addCase(createEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        const createdEvents = Array.isArray(action.payload?.events)
          ? action.payload.events
          : [action.payload];
        state.events.push(...createdEvents);
        state.loading = false;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Obtener todos los eventos
      .addCase(fetchEvents.pending, (state) => {
        state.loading = state.events.length === 0;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.events = action.payload;
        state.loading = false;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Obtener todos los eventos (Global)
      .addCase(fetchGlobalEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalEvents.fulfilled, (state, action) => {
        state.events = action.payload;
        state.loading = false;
      })
      .addCase(fetchGlobalEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Obtener detalles de un evento
      .addCase(fetchEventDetails.pending, (state) => {
        state.loading = !state.eventDetails;
        state.error = null;
      })
      .addCase(fetchEventDetails.fulfilled, (state, action) => {
        state.eventDetails = action.payload;
        state.loading = false;
      })
      .addCase(fetchEventDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.eventDetails = null;
      })

      // **Actualizar** evento
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const updatedEvent = action.payload;
        // Actualizamos el evento en la lista de events
        const index = state.events.findIndex((ev) => ev._id === updatedEvent._id);
        if (index !== -1) {
          state.events[index] = updatedEvent;
        }
        // Si estamos en la vista de detalles del mismo evento, también lo actualizamos
        if (state.eventDetails && state.eventDetails._id === updatedEvent._id) {
          state.eventDetails = updatedEvent;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addEventDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addEventDate.fulfilled, (state, action) => {
        const updatedEvent = action.payload;
        const index = state.events.findIndex((event) => event._id === updatedEvent._id);
        if (index !== -1) state.events[index] = updatedEvent;
        if (state.eventDetails?._id === updatedEvent._id) state.eventDetails = updatedEvent;
        state.loading = false;
      })
      .addCase(addEventDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(mergeEventDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mergeEventDate.fulfilled, (state, action) => {
        const { event, mergedEventId } = action.payload;
        state.events = state.events
          .filter((currentEvent) => currentEvent._id !== mergedEventId)
          .map((currentEvent) => currentEvent._id === event._id ? event : currentEvent);
        if (state.eventDetails?._id === event._id) state.eventDetails = event;
        state.loading = false;
      })
      .addCase(mergeEventDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // **Eliminar** evento
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        // Removemos el evento de la lista
        state.events = state.events.filter((ev) => ev._id !== action.payload);
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default eventSlice.reducer;
